<script setup lang="ts">
import SubwayStationsList from '@/shared/ui/SubwayStationsList/index';
import { scroll } from '@/shared/utils/scrollBehavior';
import type { IInfoLocationProps } from '@/widgets/LawyerCard/model/types/lawyerCard';

const props = withDefaults(defineProps<IInfoLocationProps>(), {
  isCatalog: false,
  isCardSlider: false,
  isLawyerPage: false,
});

const route = useRoute();
const isMobile: boolean = useIsMobile();

const isCatalogWithoutRegion: ComputedRef<boolean> = computed(
  () => props.isCatalog && !route.params.region,
);
const hasSubway: ComputedRef<boolean> = computed(() => props.subway.length > 0);
const isShowStreetBlock: ComputedRef<boolean> = computed(
  () => (!hasSubway.value && props.isCardSlider) || (props.isCatalog && !hasSubway.value),
);
const isShowScrollButton: ComputedRef<boolean> = computed(
  () =>
    (!props.isCatalog && !route.params.region && !props.isCardSlider) ||
    props.isLawyerPage,
);
const isShowSubway: ComputedRef<boolean> = computed(
  () => (hasSubway.value && !props.isCatalog) || (hasSubway.value && !props.isCardSlider),
);

const scrollToContacts = (): void => {
  const contacts = document.getElementById('contacts');
  scroll(contacts);
};
</script>

<template>
  <div
    :class="[
      'info-location',
      { _mobile: isMobile },
      { _catalog: isCatalog },
      { _slider: isCardSlider },
      { _lawyer: isLawyerPage },
    ]"
  >
    <template v-if="isCatalogWithoutRegion">
      <NuxtLink class="info-location__link" :to="linkCityCatalog">
        <div :class="['info-location__city', { '_with-subway': hasSubway }]">
          <AppIcon name="Location" />
          <span class="info-location__city-name"> {{ city }}{{ separatorSymbol }} </span>
        </div>
      </NuxtLink>
      <div v-if="isShowStreetBlock" class="info-location__address">
        <span class="info-location__street">&nbsp;{{ street }}</span>
      </div>
    </template>
    <template v-else>
      <div :class="['info-location__city', { '_with-subway': hasSubway }]">
        <button
          v-if="isShowScrollButton"
          class="info-location__address-button"
          :aria-label="`${city}: перейти к контактам`"
          @click.prevent="scrollToContacts"
        >
          <AppIcon name="Location" />
          <span class="info-location__city-name"> {{ city }}{{ separatorSymbol }} </span>
        </button>
        <template v-else>
          <AppIcon name="Location" />
          <span class="info-location__city-name"> {{ city }}{{ separatorSymbol }} </span>
        </template>
      </div>
      <div v-if="isShowStreetBlock" class="info-location__address">
        <span class="info-location__street">&nbsp;{{ street }}</span>
      </div>
    </template>
    <div v-if="isShowSubway" class="info-location__stations">
      <SubwayStationsList :stations="subway[0] ? [subway[0]] : []" />
    </div>
  </div>
</template>

<style lang="less">
.info-location {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-top: 6px;

  &__link {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  &__address-button {
    display: inline-flex;
    align-items: center;
  }

  &__city {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    font-size: @size-x3;
    line-height: @size-x4;
    color: @color-dove-gray;
    margin-left: -1px;
    margin-top: 6px;

    svg {
      width: 15px;
      height: @size-x4;

      path {
        stroke: @color-elephant;
      }
    }

    &._with-subway {
      margin-right: 10px;
    }
  }

  &__city-name {
    font-weight: 600;
    margin-left: @main-size;
  }

  &__address {
    display: inline-flex;
    margin-top: 6px;
  }

  &__street {
    font-weight: 500;
    font-size: @size-x3;
    line-height: @size-x4;
    display: inline-block;
    color: @color-dove-gray;
    text-align: left;
  }

  &__stations {
    margin-top: 6px;
  }

  &._mobile {
    align-items: center;
  }

  &._mobile._catalog {
    margin-bottom: 0;
  }

  &._mobile._slider {
    &:last-child {
      margin-bottom: 0;
    }
  }

  &._mobile &__city {
    font-size: @size-x3;
    line-height: @size-x4;
    flex-wrap: wrap;
  }

  &._mobile &__street {
    font-size: @size-x3;
    line-height: @size-x4;
  }
}
</style>
